import axios from 'axios';
import { notifySetupStateChanged } from './utils/setupStep';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Channel-manager (PMS) connection status.
 *
 * The API token and webhook secret are never returned — only whether each is set, so the UI can
 * show progress through setup without ever holding the credentials.
 */
export interface PmsConnection {
  connected: boolean;
  provider: string;
  enabled: boolean;
  hasToken: boolean;
  /**
   * The secret has been learned from a delivery and inbound webhooks are verified against it.
   * Hostex returns this value from no API, so it can only be observed on a real call.
   */
  hasWebhookSecret: boolean;
  /** The next delivery will teach us the secret. Distinguishes "waiting" from "expired". */
  secretEnrolmentOpen: boolean;
  /** A read-only token syncs reservations fine and then cannot message guests. */
  canWrite: boolean;
  webhookUrl: string | null;
  lastWebhookAt: string | null;
  lastPollAt: string | null;
  lastError: string | null;
  linkedApartments: number;
}

export interface HostexProperty {
  id: number;
  title: string;
  address?: string;
}

/** One row of the read-only apartment ↔ property overview on the connection detail page. */
export interface LinkedApartment {
  apartmentId: number;
  apartmentName: string;
  externalPropertyId: string | null;
  externalPropertyName: string | null;
}

/** What switching an apartment to the live API would remove and preserve. */
export interface PmsLinkPreview {
  requiresConfirmation: boolean;
  icalFeedsToRemove: string[];
  reservationsPreserved: number;
  reservationsDiscarded: number;
}

export interface AttentionReservation {
  id: number;
  guestName: string | null;
  checkInDate: string;
  checkOutDate: string;
  status: string | null;
  reason: string | null;
  hasGuestForm: boolean;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
  };
};

const config = () => ({ headers: getAuthHeaders(), withCredentials: true as const });

// ── Connection ───────────────────────────────────────────────────────────────

export const getHostexConnection = async (): Promise<PmsConnection> => {
  const res = await axios.get(`${API_BASE}/pms/hostex`, config());
  return res.data;
};

export const connectHostex = async (accessToken: string): Promise<PmsConnection> => {
  const res = await axios.post(`${API_BASE}/pms/hostex`, { accessToken }, config());
  return res.data;
};

export const saveHostexWebhookSecret = async (webhookSecret: string): Promise<PmsConnection> => {
  const res = await axios.put(`${API_BASE}/pms/hostex/webhook-secret`, { webhookSecret }, config());
  return res.data;
};

/**
 * Restarts secret discovery — for when the first delivery never arrived, the secret was rotated,
 * or the webhook was re-created by hand at Hostex.
 */
export const reopenSecretEnrolment = async (): Promise<PmsConnection> => {
  const res = await axios.post(`${API_BASE}/pms/hostex/reopen-secret-enrolment`, {}, config());
  return res.data;
};

export const testHostexConnection = async (): Promise<PmsConnection> => {
  const res = await axios.post(`${API_BASE}/pms/hostex/test`, {}, config());
  return res.data;
};

export const disconnectHostex = async (): Promise<void> => {
  await axios.delete(`${API_BASE}/pms/hostex`, config());
};

export const getHostexProperties = async (): Promise<HostexProperty[]> => {
  const res = await axios.get(`${API_BASE}/pms/hostex/properties`, config());
  return res.data;
};

export const getLinkedApartments = async (): Promise<LinkedApartment[]> => {
  const res = await axios.get(`${API_BASE}/pms/hostex/linked-apartments`, config());
  return res.data;
};

// ── Per-apartment mapping ────────────────────────────────────────────────────

export const previewApartmentLink = async (apartmentId: number): Promise<PmsLinkPreview> => {
  const res = await axios.get(`${API_BASE}/pms/hostex/apartments/${apartmentId}/link-preview`, config());
  return res.data;
};

export const linkApartment = async (
  apartmentId: number,
  externalPropertyId: string,
  externalPropertyName: string,
  confirmed: boolean,
  syncPastReservations: boolean
): Promise<void> => {
  await axios.post(
    `${API_BASE}/pms/hostex/apartments/${apartmentId}/link`,
    { externalPropertyId, externalPropertyName, confirmed, syncPastReservations },
    config()
  );
  notifySetupStateChanged();
};

export const unlinkApartment = async (apartmentId: number): Promise<void> => {
  await axios.delete(`${API_BASE}/pms/hostex/apartments/${apartmentId}/link`, config());
  notifySetupStateChanged();
};

/** Flips the past-reservations window without touching the property mapping. */
export const setSyncPastReservations = async (apartmentId: number, enabled: boolean): Promise<void> => {
  await axios.put(
    `${API_BASE}/pms/hostex/apartments/${apartmentId}/sync-past-reservations`,
    { enabled },
    config()
  );
};

// ── Message templates ────────────────────────────────────────────────────────

export type MessageTemplateType = 'FORM_LINK' | 'GUESTBOOK_REMINDER' | 'GUIDE_READY';

export interface MessageTemplate {
  templateType: MessageTemplateType;
  body: string;
  enabled: boolean;
  /** Days before check-in; null sends as soon as the reservation arrives. */
  sendOffsetDays: number | null;
  /** False for messages that answer an event rather than a date — no timing to choose. */
  schedulable: boolean;
  /** True when "as soon as it happens" is one of the timing choices, not just a day count. */
  allowsImmediate: boolean;
  /** True while the manager has not written their own — body is the built-in default. */
  usingDefault: boolean;
}

export interface MessagePlaceholder {
  token: string;
  /** How it is written in a template, e.g. `{{form_link}}`. */
  placeholder: string;
  description: string;
}

export const getMessageTemplates = async (): Promise<MessageTemplate[]> => {
  const res = await axios.get(`${API_BASE}/pms/message-templates`, config());
  return res.data;
};

export const getMessagePlaceholders = async (): Promise<MessagePlaceholder[]> => {
  const res = await axios.get(`${API_BASE}/pms/message-templates/placeholders`, config());
  return res.data;
};

export const saveMessageTemplate = async (
  type: MessageTemplateType,
  body: string,
  enabled: boolean,
  sendOffsetDays: number | null
): Promise<MessageTemplate> => {
  const res = await axios.put(
    `${API_BASE}/pms/message-templates/${type}`,
    { body, enabled, sendOffsetDays },
    config()
  );
  return res.data;
};

export const resetMessageTemplate = async (type: MessageTemplateType): Promise<MessageTemplate> => {
  const res = await axios.delete(`${API_BASE}/pms/message-templates/${type}`, config());
  return res.data;
};

export const previewMessageTemplate = async (body: string): Promise<string> => {
  const res = await axios.post(`${API_BASE}/pms/message-templates/preview`, { body }, config());
  return res.data.preview;
};

// ── Triage ───────────────────────────────────────────────────────────────────

export const getAttentionReservations = async (apartmentId: number): Promise<AttentionReservation[]> => {
  const res = await axios.get(`${API_BASE}/pms/hostex/apartments/${apartmentId}/attention`, config());
  return res.data;
};

export const resolveAttentionReservation = async (
  apartmentId: number,
  reservationId: number
): Promise<void> => {
  await axios.post(
    `${API_BASE}/pms/hostex/apartments/${apartmentId}/attention/${reservationId}/resolve`,
    {},
    config()
  );
};

export const removeAttentionReservation = async (
  apartmentId: number,
  reservationId: number
): Promise<void> => {
  await axios.delete(
    `${API_BASE}/pms/hostex/apartments/${apartmentId}/attention/${reservationId}`,
    config()
  );
};

export const setAttentionReservationStatus = async (
  apartmentId: number,
  reservationId: number,
  status: string
): Promise<void> => {
  await axios.put(
    `${API_BASE}/pms/hostex/apartments/${apartmentId}/attention/${reservationId}/status`,
    { status },
    config()
  );
};

export interface BulkActionResult {
  succeeded: number;
  failed: number;
}

export const bulkResolveAttention = async (
  apartmentId: number,
  reservationIds: number[]
): Promise<BulkActionResult> => {
  const res = await axios.post(
    `${API_BASE}/pms/hostex/apartments/${apartmentId}/attention/bulk-resolve`,
    { reservationIds },
    config()
  );
  return res.data;
};

export const bulkRemoveAttention = async (
  apartmentId: number,
  reservationIds: number[]
): Promise<BulkActionResult> => {
  const res = await axios.post(
    `${API_BASE}/pms/hostex/apartments/${apartmentId}/attention/bulk-remove`,
    { reservationIds },
    config()
  );
  return res.data;
};

export const bulkSetAttentionStatus = async (
  apartmentId: number,
  reservationIds: number[],
  status: string
): Promise<BulkActionResult> => {
  const res = await axios.post(
    `${API_BASE}/pms/hostex/apartments/${apartmentId}/attention/bulk-status`,
    { reservationIds, status },
    config()
  );
  return res.data;
};

// ── Duplicates ───────────────────────────────────────────────────────────────

/** One half of a duplicate pair, described by what would be lost with it. */
export interface DuplicateSide {
  id: number;
  guestName: string | null;
  source: string | null;
  formMemberCount: number;
  hasCityTax: boolean;
  hasUbyport: boolean;
  hasCheckinLink: boolean;
  hasPmsCode: boolean;
}

/** Two records of one stay. `keep` is the recommended survivor, not a fixed one. */
export interface DuplicateReservation {
  keep: DuplicateSide;
  remove: DuplicateSide;
  /** Why that orientation was recommended — the frontend supplies the wording. */
  keepHasGuestData: boolean;
  checkInDate: string;
  checkOutDate: string;
}

export const getDuplicateReservations = async (
  apartmentId: number
): Promise<DuplicateReservation[]> => {
  const res = await axios.get(`${API_BASE}/pms/hostex/apartments/${apartmentId}/duplicates`, config());
  return res.data;
};

export const reuniteReservations = async (
  apartmentId: number,
  keepId: number,
  removeId: number
): Promise<void> => {
  await axios.post(
    `${API_BASE}/pms/hostex/apartments/${apartmentId}/duplicates/reunite`,
    { keepId, removeId },
    config()
  );
};
